import { Wrench } from 'lucide-react';
import { useState } from 'react';
import { Badge } from '../components/Badge';
import { ConfirmDialog } from '../components/forms/ConfirmDialog';
import { Modal } from '../components/Modal';
import type { AdminToolDefinition, AdminToolHandler, AdminToolInput } from '../hooks/useAdminData';
import {
  useAdminTools,
  useCreateAdminTool,
  useDeleteAdminTool,
  useOrganizations,
  useToggleAdminTool,
  useToolHandlers,
  useUpdateAdminTool,
} from '../hooks/useAdminData';

/**
 * Mirrors TOOL_NAME_PATTERN in the backend. Gemini and Claude both restrict the
 * character set of a tool name, so a name the API would refuse is worth saying
 * so about while the admin is still looking at the field.
 */
const TOOL_NAME_PATTERN = /^[a-z][a-z0-9_]{2,63}$/;

const TOOL_NAME_RULE =
  'Use 3 to 64 characters of lowercase letters, digits and underscores, starting with a letter.';

const AGENT_TYPES = [
  'lead_qualification',
  'lead_enrichment',
  'follow_up_scheduler',
  'sentiment_analysis',
  'stage_prospect_intake',
  'stage_first_contact',
  'stage_qualification_deep',
  'stage_proposal_prep',
  'stage_won_onboarding',
  'stage_lost_analysis',
  'orchestrator',
] as const;

const DEFAULT_PARAMETERS_SCHEMA = JSON.stringify(
  { type: 'object', properties: {}, required: [] },
  null,
  2,
);

function formatAgentType(agentType: string) {
  return agentType
    .split('_')
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

/** Server refusals name the exact defect. Paraphrasing them destroys the fix. */
function serverMessage(error: unknown): string | null {
  if (!error) return null;

  const message = (error as { response?: { data?: { message?: string | string[] } } })?.response
    ?.data?.message;

  if (Array.isArray(message)) return message.join(' ');
  if (typeof message === 'string') return message;

  return 'The tool could not be saved. Please try again.';
}

function parseSchema(raw: string): { schema?: Record<string, unknown>; error?: string } {
  try {
    const parsed = JSON.parse(raw);

    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) {
      return { error: 'The parameters schema must be a JSON object.' };
    }

    return { schema: parsed as Record<string, unknown> };
  } catch (error) {
    return {
      error: `The parameters schema is not valid JSON: ${
        error instanceof Error ? error.message : 'unknown parse error'
      }`,
    };
  }
}

function ToggleSwitch({
  enabled,
  onToggle,
  disabled,
  label,
}: {
  enabled: boolean;
  onToggle: () => void;
  disabled?: boolean;
  label: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={enabled}
      aria-label={label}
      disabled={disabled}
      onClick={onToggle}
      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
        enabled ? 'bg-primary' : 'bg-border'
      } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
    >
      <span
        className={`inline-block h-3.5 w-3.5 rounded-full bg-white transition-transform ${
          enabled ? 'translate-x-[18px]' : 'translate-x-[3px]'
        }`}
      />
    </button>
  );
}

function ToolRow({
  tool,
  onEdit,
  onDelete,
  onToggle,
  toggling,
}: {
  tool: AdminToolDefinition;
  onEdit: () => void;
  onDelete: () => void;
  onToggle: () => void;
  toggling: boolean;
}) {
  return (
    <div
      data-testid={`tool-${tool.id}`}
      className={`bg-surface rounded-xl border border-border p-4 space-y-3 ${
        tool.isEnabled ? '' : 'opacity-60'
      }`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 min-w-0">
          <div
            className={`flex items-center justify-center w-9 h-9 rounded-lg shrink-0 ${
              tool.isEnabled ? 'bg-primary-muted' : 'bg-border'
            }`}
          >
            <Wrench size={16} className={tool.isEnabled ? 'text-primary' : 'text-text-muted'} />
          </div>
          <div className="min-w-0">
            <h3 className="text-sm font-medium text-text truncate">{tool.displayName}</h3>
            <p className="text-xs font-mono text-text-muted truncate">{tool.name}</p>
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <span className="text-xs text-text-muted">{tool.isEnabled ? 'Enabled' : 'Disabled'}</span>
          <ToggleSwitch
            enabled={tool.isEnabled}
            onToggle={onToggle}
            disabled={toggling}
            label={`${tool.isEnabled ? 'Disable' : 'Enable'} ${tool.name}`}
          />
        </div>
      </div>

      <p className="text-xs text-text-secondary">{tool.description}</p>

      <div className="flex flex-wrap items-center gap-2">
        <Badge variant="default">Handler: {tool.handlerKey}</Badge>
        <Badge variant={tool.organizationId ? 'primary' : 'default'}>
          {tool.organizationId ? 'Organization scoped' : 'Platform wide'}
        </Badge>
        {tool.requiresApproval && <Badge variant="warning">Requires approval</Badge>}
      </div>

      <div className="flex flex-wrap items-center gap-1">
        {tool.agentTypes.map((agentType) => (
          <span
            key={agentType}
            className="text-[10px] rounded border border-border px-1.5 py-0.5 text-text-muted"
          >
            {formatAgentType(agentType)}
          </span>
        ))}
      </div>

      <div className="flex items-center justify-end gap-2 pt-1">
        <button
          type="button"
          onClick={onDelete}
          className="border border-border rounded-lg px-3 py-1.5 text-xs text-text-secondary hover:text-text hover:bg-surface-hover transition-colors"
        >
          Delete
        </button>
        <button
          type="button"
          onClick={onEdit}
          className="bg-primary text-white rounded-lg px-3 py-1.5 text-xs font-medium hover:bg-primary-hover transition-colors"
        >
          Edit
        </button>
      </div>
    </div>
  );
}

const FIELD_CLASS =
  'w-full bg-background border border-border rounded-lg px-3 py-2 text-sm text-text focus:outline-none focus:border-primary';

function ToolForm({
  tool,
  handlers,
  organizations,
  onSubmit,
  submitting,
  errorMessage,
  submitLabel,
}: {
  tool: AdminToolDefinition | null;
  handlers: AdminToolHandler[];
  organizations: Array<{ id: string; name: string }>;
  onSubmit: (input: AdminToolInput) => void;
  submitting: boolean;
  errorMessage: string | null;
  submitLabel: string;
}) {
  const [name, setName] = useState(tool?.name ?? '');
  const [displayName, setDisplayName] = useState(tool?.displayName ?? '');
  const [description, setDescription] = useState(tool?.description ?? '');
  const [handlerKey, setHandlerKey] = useState(tool?.handlerKey ?? handlers[0]?.key ?? '');
  const [agentTypes, setAgentTypes] = useState<string[]>(tool?.agentTypes ?? []);
  const [organizationId, setOrganizationId] = useState(tool?.organizationId ?? '');
  const [requiresApproval, setRequiresApproval] = useState(tool?.requiresApproval ?? false);
  const [schemaText, setSchemaText] = useState(
    tool ? JSON.stringify(tool.parametersSchema, null, 2) : DEFAULT_PARAMETERS_SCHEMA,
  );
  const [submitError, setSubmitError] = useState<string | null>(null);

  const nameError = name && !TOOL_NAME_PATTERN.test(name) ? TOOL_NAME_RULE : null;
  const { schema, error: schemaError } = parseSchema(schemaText);
  const handlerDescription = handlers.find((handler) => handler.key === handlerKey)?.description;

  function toggleAgentType(agentType: string) {
    setAgentTypes((current) =>
      current.includes(agentType)
        ? current.filter((value) => value !== agentType)
        : [...current, agentType],
    );
  }

  function handleSubmit() {
    if (!name.trim() || nameError || schemaError || !schema) return;

    if (!displayName.trim() || !description.trim() || !handlerKey) {
      setSubmitError('Name, display name, description and handler are all required.');
      return;
    }

    if (agentTypes.length === 0) {
      setSubmitError('Pick at least one agent type allowed to call this tool.');
      return;
    }

    setSubmitError(null);
    onSubmit({
      name: name.trim(),
      displayName: displayName.trim(),
      description: description.trim(),
      handlerKey,
      agentTypes,
      parametersSchema: schema,
      ...(organizationId ? { organizationId } : {}),
      requiresApproval,
      isEnabled: tool?.isEnabled ?? true,
    });
  }

  return (
    <div className="space-y-4">
      <div>
        <label
          htmlFor="tool-name"
          className="block text-sm font-medium text-text-secondary mb-1.5"
        >
          Tool name
        </label>
        <input
          id="tool-name"
          type="text"
          value={name}
          onChange={(e) => setName(e.target.value)}
          aria-describedby="tool-name-rule"
          aria-invalid={Boolean(nameError)}
          className={`${FIELD_CLASS} font-mono`}
          placeholder="send_quote_email"
        />
        <p id="tool-name-rule" className="text-xs text-text-muted mt-1">
          The name the model calls. {TOOL_NAME_RULE}
        </p>
        {nameError && (
          <p data-testid="tool-name-error" role="alert" className="text-xs text-red-500 mt-1">
            {nameError}
          </p>
        )}
      </div>

      <div>
        <label
          htmlFor="tool-display-name"
          className="block text-sm font-medium text-text-secondary mb-1.5"
        >
          Display name
        </label>
        <input
          id="tool-display-name"
          type="text"
          value={displayName}
          onChange={(e) => setDisplayName(e.target.value)}
          className={FIELD_CLASS}
          placeholder="Send quote email"
        />
      </div>

      <div>
        <label
          htmlFor="tool-description"
          className="block text-sm font-medium text-text-secondary mb-1.5"
        >
          Description
        </label>
        <textarea
          id="tool-description"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          aria-describedby="tool-description-help"
          className={`${FIELD_CLASS} resize-y`}
          placeholder="What the tool does, and when to call it..."
        />
        <p id="tool-description-help" className="text-xs text-text-muted mt-1">
          This is the text the model reads to decide whether to call the tool. Vague wording here is
          the most common reason an agent calls the wrong tool — be specific about when to use it
          and when not to.
        </p>
      </div>

      <div>
        <label
          htmlFor="tool-handler"
          className="block text-sm font-medium text-text-secondary mb-1.5"
        >
          Handler
        </label>
        <select
          id="tool-handler"
          value={handlerKey}
          onChange={(e) => setHandlerKey(e.target.value)}
          aria-describedby="tool-handler-help"
          className={FIELD_CLASS}
        >
          {handlers.map((handler) => (
            <option key={handler.key} value={handler.key}>
              {handler.key}
            </option>
          ))}
        </select>
        <p id="tool-handler-help" className="text-xs text-text-muted mt-1">
          {handlerDescription ??
            'You author a tool schema, never its implementation. Every tool runs through one of the existing handlers.'}
        </p>
      </div>

      <div>
        <label
          htmlFor="tool-scope"
          className="block text-sm font-medium text-text-secondary mb-1.5"
        >
          Scope
        </label>
        <select
          id="tool-scope"
          value={organizationId}
          onChange={(e) => setOrganizationId(e.target.value)}
          className={FIELD_CLASS}
        >
          <option value="">Platform wide — every organization</option>
          {organizations.map((organization) => (
            <option key={organization.id} value={organization.id}>
              {organization.name}
            </option>
          ))}
        </select>
      </div>

      <fieldset>
        <legend className="block text-sm font-medium text-text-secondary mb-1.5">
          Agent types allowed to call this tool
        </legend>
        <div className="grid grid-cols-2 gap-1.5">
          {AGENT_TYPES.map((agentType) => (
            <div key={agentType} className="flex items-center gap-2">
              <input
                id={`tool-agent-${agentType}`}
                type="checkbox"
                checked={agentTypes.includes(agentType)}
                onChange={() => toggleAgentType(agentType)}
              />
              <label htmlFor={`tool-agent-${agentType}`} className="text-xs text-text-secondary">
                {formatAgentType(agentType)}
              </label>
            </div>
          ))}
        </div>
      </fieldset>

      <div className="flex items-start gap-2">
        <input
          id="tool-requires-approval"
          type="checkbox"
          checked={requiresApproval}
          onChange={(e) => setRequiresApproval(e.target.checked)}
          className="mt-0.5"
        />
        <label htmlFor="tool-requires-approval" className="text-sm text-text-secondary">
          Require human approval before executing
        </label>
      </div>

      <div>
        <label
          htmlFor="tool-parameters-schema"
          className="block text-sm font-medium text-text-secondary mb-1.5"
        >
          Parameters JSON Schema
        </label>
        <textarea
          id="tool-parameters-schema"
          value={schemaText}
          onChange={(e) => setSchemaText(e.target.value)}
          rows={10}
          aria-invalid={Boolean(schemaError)}
          className={`${FIELD_CLASS} font-mono resize-y leading-relaxed`}
        />
        {schemaError && (
          <p
            data-testid="parameters-schema-error"
            role="alert"
            className="text-xs text-red-500 mt-1"
          >
            {schemaError}
          </p>
        )}
      </div>

      {submitError && (
        <p role="alert" className="text-sm text-red-500">
          {submitError}
        </p>
      )}

      {errorMessage && (
        <p role="alert" className="text-sm text-red-500 whitespace-pre-wrap">
          {errorMessage}
        </p>
      )}

      <div className="flex justify-end pt-2">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={submitting}
          className="bg-primary text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-primary-hover transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {submitting ? 'Saving...' : submitLabel}
        </button>
      </div>
    </div>
  );
}

export function AgentTools() {
  const { data: tools, isLoading } = useAdminTools();
  const { data: handlers } = useToolHandlers();
  const { data: organizations } = useOrganizations({ limit: 100 });

  const createMutation = useCreateAdminTool();
  const updateMutation = useUpdateAdminTool();
  const toggleMutation = useToggleAdminTool();
  const deleteMutation = useDeleteAdminTool();

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<AdminToolDefinition | null>(null);
  const [deleting, setDeleting] = useState<AdminToolDefinition | null>(null);

  function openCreate() {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(tool: AdminToolDefinition) {
    setEditing(tool);
    setFormOpen(true);
  }

  function handleSubmit(input: AdminToolInput) {
    if (editing) {
      updateMutation.mutate(
        { id: editing.id, data: input },
        { onSuccess: () => setFormOpen(false) },
      );
      return;
    }

    createMutation.mutate(input, { onSuccess: () => setFormOpen(false) });
  }

  function handleDelete() {
    if (!deleting) return;
    deleteMutation.mutate(deleting.id, { onSuccess: () => setDeleting(null) });
  }

  const activeMutation = editing ? updateMutation : createMutation;

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Agent Tools</h1>
          <p className="text-sm text-text-muted mt-1">
            Author the tools agents can call. You define the schema, description and approval
            policy; every tool runs through an existing handler.
          </p>
        </div>
        <button
          type="button"
          onClick={openCreate}
          className="bg-primary text-white rounded-lg px-4 py-2 text-sm font-medium hover:bg-primary-hover transition-colors shrink-0"
        >
          New tool
        </button>
      </div>

      {isLoading && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div
              key={i}
              className="bg-surface rounded-xl border border-border p-4 h-40 animate-pulse"
            >
              <div className="h-4 bg-border rounded w-40" />
            </div>
          ))}
        </div>
      )}

      {!isLoading && tools && tools.length === 0 && (
        <p className="text-text-muted">
          No authored tools yet. Code-defined tools are always available and are not listed here.
        </p>
      )}

      {!isLoading && !tools && <p className="text-text-muted">Failed to load agent tools.</p>}

      {tools && tools.length > 0 && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {tools.map((tool) => (
            <ToolRow
              key={tool.id}
              tool={tool}
              onEdit={() => openEdit(tool)}
              onDelete={() => setDeleting(tool)}
              onToggle={() => toggleMutation.mutate(tool.id)}
              toggling={toggleMutation.isPending}
            />
          ))}
        </div>
      )}

      <Modal
        open={formOpen}
        onClose={() => setFormOpen(false)}
        title={editing ? `Edit ${editing.name}` : 'Author a new tool'}
        size="xl"
      >
        <ToolForm
          key={editing?.id ?? 'new'}
          tool={editing}
          handlers={handlers ?? []}
          organizations={organizations?.data ?? []}
          onSubmit={handleSubmit}
          submitting={activeMutation.isPending}
          errorMessage={serverMessage(activeMutation.error)}
          submitLabel={editing ? 'Save tool' : 'Create tool'}
        />
      </Modal>

      <ConfirmDialog
        open={deleting !== null}
        onClose={() => setDeleting(null)}
        onConfirm={handleDelete}
        title={`Delete ${deleting?.name ?? 'tool'}`}
        message="Agents lose this tool immediately. Runs already in flight that were about to call it will fail. This cannot be undone."
        confirmLabel="Delete tool"
        confirmVariant="danger"
        loading={deleteMutation.isPending}
      />
    </div>
  );
}
